import React from "react";
import { Helmet } from "react-helmet-async";
import LoadingIndicator from "../LoadingIndicator/LoadingIndicator";

interface PageWrapperProps {
  title?: string;
  subtitle?: string;
  subtitleSecondLine?: string;
  loading?: boolean;
  error?: string | null;
  children: React.ReactNode;
  actions?: React.ReactNode;
  hideHeader?: boolean;
  titleHelpIcon?: React.ReactNode;
  className?: string;
  stickyContent?: React.ReactNode;
  fullWidth?: boolean;
  sidebar?: React.ReactNode;
}

const MainPageWrapper: React.FC<PageWrapperProps> = ({
  title,
  subtitle,
  subtitleSecondLine,
  loading,
  children,
  actions,
  hideHeader,
  titleHelpIcon,
  className,
  stickyContent,
  fullWidth,
  sidebar,
}) => {
  const pageTitle = title || "CertainPath";
  const showHeaderBlock = (!!title || !!actions) && !hideHeader;
  // Full-bleed is opt-in via `fullWidth` only — a sidebar alone stays capped.
  const isFullWidth = Boolean(fullWidth);
  const containerWidth = isFullWidth ? "w-full" : "max-w-7xl mx-auto";

  const headerRow = showHeaderBlock ? (
    <div className="flex items-center justify-between h-12">
      <div className="flex flex-col justify-center min-w-0 leading-tight">
        <div className="flex items-center gap-2 min-w-0">
          {title && (
            <h1 className="text-[15px] font-medium truncate text-gray-900 dark:text-slate-100">
              {title}
            </h1>
          )}
          {titleHelpIcon && (
            <span className="text-gray-500 dark:text-slate-400">
              {titleHelpIcon}
            </span>
          )}
          {subtitle && (
            <span className="hidden sm:inline text-sm text-gray-500 dark:text-slate-400">
              — {subtitle}
            </span>
          )}
        </div>
        {subtitleSecondLine && (
          <span className="hidden sm:block text-sm text-gray-500 dark:text-slate-400 truncate">
            {subtitleSecondLine}
          </span>
        )}
      </div>
      {actions && (
        <div className="flex-shrink-0 ml-4 [&_button]:h-7 [&_button]:px-2.5 [&_button]:text-xs">
          {actions}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className={`relative min-h-full ${className || ""}`}>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>

      {headerRow && (
        <div className="border-b border-gray-200/60 dark:border-slate-700/60 bg-gray-50 dark:bg-slate-950 sticky top-0 z-[5]">
          <div className={`${containerWidth} px-4 sm:px-6 lg:px-8`}>
            {headerRow}
          </div>
        </div>
      )}

      {stickyContent && (
        <div className="sticky top-12 z-[4] border-b border-gray-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900">
          <div className={`${containerWidth} px-4 sm:px-6 lg:px-8 py-3`}>
            {stickyContent}
          </div>
        </div>
      )}

      {sidebar ? (
        <div className={containerWidth}>
          {/* Stacks on narrow viewports so a side rail becomes a section of
              the page rather than squeezing the content beside it. The
              breakpoint is `md` (768px) deliberately: it must match
              `useIsMobile()`'s MOBILE_BREAKPOINT, because rails such as
              ResourceLibraryV2's key their desktop styling off that hook. At
              `lg` they would render desktop rail styling into a stacked
              column for the whole 768–1023px band. */}
          <div className="flex flex-col md:flex-row">
            {sidebar}
            <div className="flex-1 min-w-0 px-4 py-4 sm:px-6 lg:px-8">
              {children}
            </div>
          </div>
        </div>
      ) : (
        <div className={`${containerWidth} px-4 py-4 sm:px-6 lg:px-8`}>
          {children}
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/75">
          <LoadingIndicator />
        </div>
      )}
    </div>
  );
};

export default MainPageWrapper;
